/* towsons-discover.js — the pages where somebody has stopped buying, turned
 * back into a shop (Philip, 29 Sep 2026: "the idea is to encourage a sale").
 *
 *   - A search that found nothing (snippets/towsons-discover-personalised):
 *     each personalised mug drawn with their words by the product page's own
 *     renderer and the product's own box rules (towsons-personaliser.js →
 *     TowsonsPersonaliser.preview), so what they see here is what the product
 *     page will draw. Drawn as each card nears the screen, not all at once on
 *     a phone. A mug their words do not fit keeps its photo, and its link
 *     loses the words; if none fits, the heading stops promising their words.
 *
 *   - "Whose name should go on it?" — the empty basket, its page and the
 *     screen the basket icon opens (Philip, 29 Sep 2026: "The empty basket
 *     needs to be full of interesting mugs or maybe a personalised selection
 *     and ask user for a name"). The same mugs, drawn again with each name as
 *     it is typed — a signed-in customer's own first name to start, then the
 *     last name typed in this tab. A newer name always wins over a slower,
 *     older drawing. The renderer is fetched only once there is a name.
 *
 *   - The empty basket's occasion row (snippets/towsons-discover-products):
 *     "Order by Mon 26 Oct, 1pm for Halloween delivery", worked out by the
 *     delivery promise's own engine (TowsonsDelivery.lines) from the rules the
 *     page already carries — the date the product page and the checkout say.
 *     No rules, or another occasion by now: the row keeps its plain title.
 *
 *   - The basket icon's empty screen (snippets/mini-cart) asks the shop for
 *     its rows (sections/towsons-empty-basket, through Shopify's Section
 *     Rendering API) only when it is opened: the basket is on every page, and
 *     most visitors never open an empty one. Asked once per page; the theme
 *     redraws the basket's HTML on every change, and an emptied basket gets
 *     them back from memory.
 *
 * Loaded on every page (layout/theme.liquid) and by the snippets that need
 * it; loaded twice, it is the same one.
 */
(function (root, factory) {
  var isNode = typeof module === 'object' && module && module.exports;
  if (root && root.TowsonsDiscover && !isNode) return;
  var api = factory(root);
  if (isNode) module.exports = api;
  if (root) root.TowsonsDiscover = api;
})(typeof window !== 'undefined' ? window : null, function (win) {
  'use strict';
  // What the shop sells, in its own word (its config, from the portal: lib/storefront/shopWords) — "mug" unless it says otherwise.
  function shopWord(one) { var w = (win && win.Towsons && win.Towsons.words) || {}; return one ? (w.item || 'mug') : (w.items || 'mugs'); }

  var NAME_KEY = 'towsons-name';

  // The link, with the words for the product page's box (?personalise=) or
  // without them.
  function linkWith(card, words) {
    try {
      var u = new URL(card.getAttribute('href'), win.location.href);
      if (words) u.searchParams.set('personalise', words);
      else u.searchParams.delete('personalise');
      card.setAttribute('href', u.pathname + u.search + u.hash);
    } catch (e) { /* keep the link as it is */ }
  }

  // The mug's own photo back, if a drawing had taken its place.
  function photo(card) {
    var pic = card.querySelector('[data-tdc-pic]');
    if (pic && card.__photo != null && pic.querySelector('canvas')) pic.innerHTML = card.__photo;
    card.classList.remove('is-drawn');
  }

  // Their words do not fit this mug: its own photo, and a link without them.
  function plain(card) {
    photo(card);
    card.classList.add('is-plain');
    linkWith(card, '');
  }

  function whenNear(el, fn) {
    if (!('IntersectionObserver' in win)) { fn(); return; }
    var io = new win.IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); fn(); }
    }, { rootMargin: '200px' });
    io.observe(el);
  }

  var SCRIPTS = {};
  function load(src) {
    if (!SCRIPTS[src]) {
      SCRIPTS[src] = new Promise(function (resolve, reject) {
        var s = win.document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = function () { resolve(); };
        s.onerror = function () { delete SCRIPTS[src]; reject(new Error('could not load ' + src)); };
        win.document.head.appendChild(s);
      });
    }
    return SCRIPTS[src];
  }

  // The product page's renderer: on the page already, or fetched now.
  function renderer(el) {
    var ok = function () { var P = win.TowsonsPersonaliser; return P && P.preview && P.wordsFor ? P : null; };
    if (ok()) return Promise.resolve(ok());
    var src = el.getAttribute('data-personaliser-src');
    if (!src) return Promise.resolve(null);
    return load(src).then(ok, function () { return null; });
  }

  // Every mug in a section, drawn with these words — or back to its photo
  // when there are none. A newer call wins over an older one still drawing.
  function draw(el, words) {
    var gen = el.__gen = (el.__gen || 0) + 1;
    var live = function () { return el.__gen === gen; };
    el.setAttribute('data-words', words);
    Array.prototype.forEach.call(el.querySelectorAll('[data-tdc-w]'), function (s) { s.textContent = words; });
    var cards = Array.prototype.slice.call(el.querySelectorAll('[data-tdc-card]'));
    if (!words) {
      cards.forEach(function (card) { photo(card); card.classList.remove('is-plain', 'is-drawing'); linkWith(card, ''); });
      el.classList.remove('is-drawn');
      el.classList.add('is-plain');
      return Promise.resolve();
    }
    return renderer(el).then(function (P) {
      if (!live()) return;
      var scripts = { fabric: el.getAttribute('data-fabric-src'), tcd: el.getAttribute('data-tcd-src') };
      var left = cards.length;
      var drawn = 0;
      var settle = function () {
        if (!live()) return;
        left--;
        if (left > 0) return;
        el.classList.remove(drawn ? 'is-plain' : 'is-drawn');
        el.classList.add(drawn ? 'is-drawn' : 'is-plain');
      };
      if (!left) { el.classList.add('is-plain'); return; }
      var fits = 0;
      cards.forEach(function (card) {
        var cfg = null;
        try { cfg = JSON.parse(card.getAttribute('data-config') || 'null'); } catch (e) { cfg = null; }
        if (!P || !cfg || !P.wordsFor(cfg, words)) { plain(card); settle(); return; }
        fits++;
        card.classList.remove('is-plain');
        linkWith(card, words);
        card.classList.add('is-drawing');
        whenNear(card, function () {
          if (!live()) return;
          var pic = card.querySelector('[data-tdc-pic]');
          var width = Math.max(160, Math.min(520, Math.round((pic && pic.clientWidth) || 320)));
          P.preview(cfg, scripts, words, { variantId: card.getAttribute('data-variant-id') || null, width: width, aspect: 1 }).then(function (cv) {
            if (!live()) return;
            if (!cv) { plain(card); return; }
            cv.className = 'tdc-card__canvas';
            cv.setAttribute('role', 'img');
            cv.setAttribute('aria-label', (card.getAttribute('data-title') || shopWord(true).charAt(0).toUpperCase() + shopWord(true).slice(1)) + ', with “' + words + '” on it');
            if (pic) {
              if (card.__photo == null) card.__photo = pic.innerHTML;
              pic.textContent = '';
              pic.appendChild(cv);
            }
            card.classList.add('is-drawn');
            drawn++;
          }, function () { if (live()) plain(card); }).then(function () {
            if (live()) card.classList.remove('is-drawing');
            settle();
          });
        });
      });
      // The heading says their words as soon as a mug takes them (Philip, 4 Oct 2026:
      // "Change straight away"), not once every mug is drawn: a row's mugs off to the
      // side draw only when swiped to. Should every drawing fail, settle() puts the
      // plain heading back.
      if (fits) { el.classList.remove('is-plain'); el.classList.add('is-drawn'); }
    });
  }

  function clean(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, 30); }

  // "Whose name should go on it?": a name to start with (the last one typed
  // in this tab, else a signed-in customer's own), then each name as it is
  // typed — after a pause, or at once on Enter or "Show me". Enter never
  // reaches a form: the basket icon's screen is the basket's own form.
  function ask(el) {
    var input = el.querySelector('[data-tdc-name]');
    var go = el.querySelector('[data-tdc-go]');
    var saved = '';
    try { saved = clean(win.sessionStorage.getItem(NAME_KEY)); } catch (e) { saved = ''; }
    if (input && saved) input.value = saved;
    var current = null;
    var timer = null;
    var run = function () {
      if (timer) { clearTimeout(timer); timer = null; }
      var w = clean(input ? input.value : el.getAttribute('data-words'));
      if (w === current) return;
      current = w;
      try {
        if (w) win.sessionStorage.setItem(NAME_KEY, w);
        else if (input) win.sessionStorage.removeItem(NAME_KEY);   // cleared by them: forgotten
      } catch (e) { /* this tab forgets it */ }
      draw(el, w);
    };
    if (input) {
      input.addEventListener('input', function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(run, 650);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        run();
      });
    }
    if (go) go.addEventListener('click', function (e) { e.preventDefault(); run(); });
    run();
  }

  function personalised(el) {
    if (!el || el.__tdc) return;
    el.__tdc = true;
    if (el.hasAttribute('data-tdc-ask')) { ask(el); return; }
    draw(el, clean(el.getAttribute('data-words')));
  }

  function occasion(el) {
    if (!el || el.__tdc) return;
    el.__tdc = true;
    var E = win.TowsonsDelivery;
    var node = win.document.getElementById('towsons-delivery-rules');
    if (!E || !E.lines || !node) return;
    var L = null;
    try { L = E.lines(JSON.parse(node.textContent), Date.now(), { country: el.getAttribute('data-country') || 'GB', extra: 0 }); } catch (e) { L = null; }
    var o = L && L.occasion;
    if (!o || !o.pieces || String(o.name) !== el.getAttribute('data-tdc-occasion')) return;
    el.textContent = '';
    o.pieces.forEach(function (p) {
      var n = p.strong ? win.document.createElement('strong') : win.document.createTextNode(p.text);
      if (p.strong) n.textContent = p.text;
      el.appendChild(n);
    });
    el.hidden = false;
  }

  function scan(rootNode) {
    var r = rootNode || (win && win.document);
    if (!r || !r.querySelectorAll) return;
    Array.prototype.forEach.call(r.querySelectorAll('[data-tdc-personalised]'), personalised);
    Array.prototype.forEach.call(r.querySelectorAll('[data-tdc-occasion]'), occasion);
  }

  // ── the basket icon's empty screen ────────────────────────────────────

  var BASKET = { html: null, pending: null };

  function rows(src) {
    if (BASKET.html != null) return Promise.resolve(BASKET.html);
    if (!BASKET.pending) {
      BASKET.pending = win.fetch(src, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) { BASKET.html = html; return html; }, function (e) { BASKET.pending = null; throw e; });
    }
    return BASKET.pending;
  }

  // Open and empty: the rows go in (from memory once they have been asked
  // for). Anything else — shut, or a basket with something in it — waits.
  function basket(drawer) {
    var d = drawer || (win.document && win.document.querySelector('.mini-cart'));
    if (!d || d.getAttribute('aria-hidden') !== 'false') return;
    var slot = d.querySelector('[data-tdc-basket-slot]');
    if (!slot || slot.__tdc) return;
    slot.__tdc = true;
    var box = slot.closest('.mini-cart__content--empty');
    if (box) box.classList.add('tdc-basket-on');
    if (BASKET.html == null) slot.classList.add('is-loading');
    rows(slot.getAttribute('data-src')).then(function (html) {
      if (!slot.isConnected) return;
      slot.classList.remove('is-loading');
      slot.innerHTML = html;
      scan(slot);
    }, function () {
      // Not this time: the empty screen as it was, and another try on the
      // next opening.
      slot.__tdc = false;
      slot.classList.remove('is-loading');
      if (box) box.classList.remove('tdc-basket-on');
    });
  }

  function watchBasket() {
    var d = win.document.querySelector('.mini-cart');
    if (!d || d.__tdcWatch || !win.MutationObserver) return;
    d.__tdcWatch = true;
    // Opened (aria-hidden), or redrawn by the theme (its HTML swapped).
    new win.MutationObserver(function () { basket(d); }).observe(d, { attributes: true, attributeFilter: ['aria-hidden'], childList: true });
    // Asked for as the thumb goes down on the basket icon, not after.
    win.document.addEventListener('pointerdown', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[aria-controls="mini-cart"]') : null;
      var slot = t ? d.querySelector('[data-tdc-basket-slot]') : null;
      if (slot) rows(slot.getAttribute('data-src')).catch(function () { /* asked again on opening */ });
    }, true);
    basket(d);
  }

  if (win && win.document) {
    var start = function () { scan(); watchBasket(); };
    if (win.document.readyState === 'loading') win.document.addEventListener('DOMContentLoaded', start);
    else start();
    win.document.addEventListener('shopify:section:load', function (e) { scan(e.target); watchBasket(); });
  }

  return { scan: scan, personalised: personalised, occasion: occasion, plain: plain, draw: draw, ask: ask, basket: basket, NAME_KEY: NAME_KEY };
});
